/**
 * TerminalSession — Raw Terminal tab for claude_code / codex instances.
 *
 * Workflow:
 *   1. POST /agents/:agentId/command  command_type=start_pty
 *   2. Receive command_id from the response.
 *   3. Mount the existing <Terminal> component which:
 *       - subscribes SSE on /agents/:agentId/logs/:commandId
 *       - posts user keystrokes (base64) on .../stdin
 *       - posts resize events the same way
 *   4. On unmount, POST close_pty to terminate the subprocess.
 *
 * The agent-side handler (handleStartPty) writes PTY output via the
 * existing log_stream pipeline, so the SSE feed is exactly what the
 * install-PTY flow already uses.
 */

import { useCallback, useEffect, useState } from 'react';
import Terminal from './Terminal';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';

interface TerminalSessionProps {
  instanceId: string;
  instanceName: string;
  agentId: string;
  isActive: boolean;
}

export default function TerminalSession({
  instanceId,
  instanceName,
  agentId,
  isActive,
}: TerminalSessionProps) {
  const { getHeaders } = useApiHeaders();
  const [commandId, setCommandId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Start the PTY on mount (when the tab activates).
  useEffect(() => {
    if (!isActive || commandId || starting) return;
    let cancelled = false;
    (async () => {
      setStarting(true);
      try {
        const headers = await getHeaders();
        const res = await fetch(`${API_URL}/api/v1/agents/${agentId}/command`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            command_type: 'start_pty',
            command_data: { instance_name: instanceName, instance_id: instanceId },
          }),
        });
        if (!res.ok) {
          throw new Error(`start_pty failed: ${res.status}`);
        }
        const data = await res.json() as { command_id: string };
        if (!cancelled) setCommandId(data.command_id);
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message ?? e));
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isActive, commandId, starting, agentId, instanceName, instanceId, getHeaders]);

  // Close the PTY on unmount or when the tab deactivates.
  const closePty = useCallback(async () => {
    if (!commandId) return;
    try {
      const headers = await getHeaders();
      await fetch(`${API_URL}/api/v1/agents/${agentId}/command`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command_type: 'close_pty',
          command_data: { command_id: commandId },
        }),
      });
    } catch {
      // Best-effort cleanup; agent will also clean up when the subprocess exits.
    }
    setCommandId(null);
  }, [agentId, commandId, getHeaders]);

  useEffect(() => {
    return () => { void closePty(); };
    // We want this to run only on unmount, not on closePty identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <div className="p-3 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      </div>
    );
  }

  if (!commandId) {
    return (
      <div className="p-6 text-sm text-slate-400 dark:text-slate-600">
        Starting terminal session…
      </div>
    );
  }

  return (
    <div className="h-[600px] p-2">
      <Terminal
        agentId={agentId}
        commandId={commandId}
        isActive={isActive}
        onClose={closePty}
      />
    </div>
  );
}
